import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Zap, Loader2, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import { sendZap } from '../../services/zapService';
import { useAuth } from '../../contexts/AuthContext';

interface ZapModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetPubkey: string;
    targetName: string;
    lnAddress?: string; // lud16 from metadata
    eventId?: string; // optional for event zapping
}

export function ZapModal({ isOpen, onClose, targetPubkey, targetName, lnAddress, eventId }: ZapModalProps) {
    const { sk } = useAuth();
    const [amount, setAmount] = useState('1000');
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'invoice' | 'success' | 'error'>('idle');
    const [invoice, setInvoice] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [copied, setCopied] = useState(false);

    const handleZap = async () => {
        if (!lnAddress) {
            setErrorMessage('Bu kullanıcının bir Lightning adresi tanımlı değil.');
            setStatus('error');
            return;
        }

        setStatus('loading');
        try {
            const result = await sendZap({
                recipientPubkey: targetPubkey,
                lnAddress,
                amount: Number(amount),
                comment: comment || undefined,
                eventId,
                sk
            });

            if (!result.success) {
                setErrorMessage(result.error || 'Zap gönderilemedi.');
                setStatus('error');
                return;
            }

            if (result.paid) {
                // WebLN payment successful
                setStatus('success');
            } else if (result.invoice) {
                // Need manual payment
                setInvoice(result.invoice);
                setStatus('invoice');
            }

        } catch (error: any) {
            setErrorMessage(error.message || 'Fatura oluşturulamadı.');
            setStatus('error');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(invoice);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="w-full max-w-sm bg-white dark:bg-surface-900 rounded-2xl shadow-2xl overflow-hidden border border-surface-200 dark:border-surface-700"
            >
                {/* Header */}
                <div className="bg-mavi-600 p-4 flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 fill-white" />
                        <h2 className="font-poppins font-bold text-lg">Bahşiş Gönder</h2>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {status === 'idle' || status === 'loading' ? (
                        <>
                            <div className="text-center">
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-merriweather">
                                    <span className="font-bold text-gray-900 dark:text-white">{targetName}</span> kullanıcısına destek ol.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">Miktar (Sats)</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {['100', '1000', '5000'].map(val => (
                                        <button
                                            key={val}
                                            onClick={() => setAmount(val)}
                                            className={`py-2 rounded-lg border text-sm font-poppins font-medium transition-all ${amount === val
                                                ? 'bg-mavi-500 border-mavi-600 text-white shadow-md'
                                                : 'bg-surface-50 dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-gray-600 dark:text-gray-300 hover:border-mavi-400'}`}
                                        >
                                            {Number(val).toLocaleString()}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="Özel miktar..."
                                    className="w-full px-4 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg focus:ring-2 focus:ring-mavi-500 outline-none transition-all text-sm font-poppins"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">Mesaj (Opsiyonel)</label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Bir not bırak..."
                                    rows={2}
                                    className="w-full px-4 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg focus:ring-2 focus:ring-mavi-500 outline-none transition-all text-sm font-merriweather resize-none"
                                />
                            </div>

                            <button
                                onClick={handleZap}
                                disabled={status === 'loading' || !amount || Number(amount) <= 0}
                                className="w-full py-3 bg-gradient-to-br from-mavi-500 to-mavi-600 hover:from-mavi-600 hover:to-mavi-700 text-white rounded-xl font-poppins font-bold shadow-lg shadow-mavi-500/20 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {status === 'loading' ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4 fill-white" />
                                        {Number(amount).toLocaleString()} Sat Gönder
                                    </>
                                )}
                            </button>
                        </>
                    ) : status === 'invoice' ? (
                        <div className="space-y-4 text-center">
                            <div className="bg-surface-50 dark:bg-surface-800 p-4 rounded-xl border border-surface-200 dark:border-surface-700 break-all">
                                <p className="text-[10px] font-mono text-gray-500 mb-2 truncate">{invoice}</p>
                                <button
                                    onClick={handleCopy}
                                    className="px-4 py-2 bg-mavi-500 text-white rounded-lg text-xs font-poppins font-bold flex items-center gap-2 mx-auto"
                                >
                                    {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                    Faturayı Kopyala
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-merriweather px-4">
                                Lütfen cüzdanınızdan ödeme yapın. Ödeme tamamlandığında bu pencere kapanacaktır.
                            </p>
                            <a
                                href={`lightning:${invoice}`}
                                className="inline-block text-mavi-500 font-poppins font-bold text-sm hover:underline"
                            >
                                Cüzdanı Aç
                            </a>
                        </div>
                    ) : status === 'success' ? (
                        <div className="py-8 text-center space-y-4">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto text-green-600">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">Başarılı!</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-merriweather">Desteğin için teşekkürler.</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="px-8 py-2 bg-surface-100 dark:bg-surface-800 rounded-lg font-poppins font-bold text-sm"
                            >
                                Kapat
                            </button>
                        </div>
                    ) : (
                        <div className="py-8 text-center space-y-4">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto text-red-600">
                                <AlertCircle className="w-10 h-10" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">Hata</h3>
                                <p className="text-sm text-red-500 font-merriweather px-4">{errorMessage}</p>
                            </div>
                            <div className="flex gap-2 justify-center">
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="px-6 py-2 bg-surface-100 dark:bg-surface-800 rounded-lg font-poppins font-bold text-sm"
                                >
                                    Tekrar Dene
                                </button>
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2 bg-mavi-500 text-white rounded-lg font-poppins font-bold text-sm"
                                >
                                    Kapat
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}

